import { useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import {
  transactionExportFiltersSchema,
  type TransactionExportFilters as ExportFilters,
} from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useExportTransactions } from '@/hooks/transaction/use-export-transactions';
import { TransactionExportFilters } from './transaction-export-filters';

/** Export owns its filters; editing them must never change the Historic feed. */
export function ExportTransactionsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [filters, setFilters] = useState<ExportFilters>({});
  const parsedFilters = useMemo(
    () => transactionExportFiltersSchema.safeParse(filters),
    [filters]
  );
  const { exportTransactions, isExporting, error } = useExportTransactions();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export CSV</DialogTitle>
          <DialogDescription>
            Download a CSV of sent and received token transfers
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (parsedFilters.success)
              void exportTransactions(parsedFilters.data);
          }}
        >
          <TransactionExportFilters
            filters={filters}
            onChange={setFilters}
            disabled={isExporting}
            {...(!parsedFilters.success
              ? {
                  error:
                    parsedFilters.error.issues[0]?.message ??
                    'Check the export filters.',
                }
              : {})}
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              type="submit"
              disabled={isExporting || !parsedFilters.success}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="mr-2 h-4 w-4" aria-hidden />
              )}
              {isExporting ? 'Exporting CSV…' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
